import { useEffect, useMemo, useState } from 'react';
import {
  DashboardJobsStatus,
  DashboardJobsType,
  useDashboardJobInfo,
  useGetDashboardJobs,
} from '~/domain/DashboardJobs';

interface IUseJobListProps {
  status: DashboardJobsStatus;
  jobType: DashboardJobsType;
}

export const useJobList = ({ status, jobType }: IUseJobListProps) => {
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isPending } = useGetDashboardJobs({
    limit: 15,
    page: currentPage,
    status,
    jobType,
  });
  const info = useDashboardJobInfo(jobType);

  const totalPages = useMemo(() => {
    let counts = 0;

    switch (status) {
      case DashboardJobsStatus.ACTION_REQUIRED:
        counts = info?.pendingCount || 0;
        break;
      case DashboardJobsStatus.FILLED:
        counts = info?.filledCount || 0;
        break;
      case DashboardJobsStatus.OPEN:
        counts = info?.openCount || 0;
        break;
    }

    return Math.ceil(counts / 15);
  }, [info?.filledCount, info?.openCount, info?.pendingCount, status]);

  useEffect(() => {
    setCurrentPage(1);
  }, [jobType]);

  return {
    data,
    // "No rows to show yet", not "a request is in flight" — so it matches the repo-wide
    // `isInitialLoading` contract that every sibling list hook exposes, and consumers can
    // swap the whole tab for a spinner without blanking the list on each background poll.
    // A cold load and a page change to an uncached page are both still isPending.
    isInitialLoading: isPending,
    methods: {
      setCurrentPage,
    },
    pagination: {
      currentPage,
      totalPages,
    },
  };
};
